import { useCallback, useEffect, useId, useMemo, useState } from 'react';
import {
  Badge,
  Button,
  Calendar,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DropdownField,
  Skeleton,
} from '@roger/ui-components';
import type { VirtualizedComboboxOption } from '@roger/ui-components';
import { AlertCircle, Plus, X } from 'lucide-react';

import { isOrderType } from '../constants/visitTypes';
import type {
  BlockedPlannedVisit,
  EpisodeLupaPeriodResponse,
  TaskListResponse,
  VisitTypeId,
} from '../services/api/types.gen';
import {
  optionTypeId,
  withAliasOptions,
  type TaskTypeOption,
} from '../hooks/useVisitTypeOptions';
import {
  TASK_TYPE_VISIT_FILTER_TABS,
  createDefaultTaskTypeFilter,
  type TaskTypeFilterValue,
} from './task/taskTypeFilters';
import { TaskTypeCombobox } from './task/TaskTypeCombobox';
import type { CalendarCreateVisitClinician } from './CalendarCreateVisitDialog';
import { cliniciansToDropdownOptions } from '../utils/clinicianUtils';
import {
  formatDateToISO,
  formatDateToMMDDYYYY,
  parseISOToLocalDate,
} from '../utils/dateUtils';
import { bulkCreatePatientVisitsPatientsPatientIdVisitsBulkPost } from '../services/api/sdk.gen';
import { checkPlannedVisits } from '../services/api';
import { getBusinessErrorMessage } from '../utils/apiErrorDetail';
import { isRecertVisitType, recertWindow } from '../utils/recertWindow';
import { useToast } from '../hooks/useToastContext';
import {
  socConflictCopy,
  socConflictCopyFromError,
} from './visit/socConflictCopy';
import { useCanViewSchedulingPointLoads } from '../hooks/useCanViewSchedulingPointLoads';
import {
  buildBulkVisitItems,
  countNewBillableVisitsByPeriod,
  countOasisSocVisits,
  countVisits,
  setDatesForType,
  stagedGroupKey,
  type StagedVisitGroup,
} from './scheduleVisitsBuilder';
import LupaThresholdGauge from './LupaThresholdGauge';
import EpisodeCalendarLegend, {
  EpisodeCalendarLegendItem,
} from './EpisodeCalendarLegend';
import {
  groupVisitsByDate,
  useEpisodeCalendarMarkers,
  visitDayKinds,
} from './useEpisodeCalendarMarkers';

interface ScheduleVisitsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  patientId: string;
  episodeId: string;
  episodeStartDate?: string | null;
  episodeEndDate?: string | null;
  /** Merged task type options; order types are filtered out internally. */
  visitTypeOptions: TaskTypeOption[];
  clinicians: CalendarCreateVisitClinician[];
  /** Display names shared by more than one clinician (shows email to disambiguate). */
  duplicateClinicianNames?: Set<string>;
  /** Called after visits are created so the parent can refetch. */
  onCreated: () => void | Promise<unknown>;
  /**
   * Whether the episode's insurance requires an authorization at all. When
   * false the pre-submit check is skipped entirely (nothing can block), so the
   * dialog doesn't call the server on every date click for the common case.
   */
  requiresAuthorization?: boolean;
  /** Day Roger charts this episode from; calendar markers when it straddles. */
  rogerChartsFrom?: string | null;
  /** The episode's per-30-day-period LUPA rows, as the Tasks calendar shows them. */
  lupaPeriods?: EpisodeLupaPeriodResponse[];
  /** True while `lupaPeriods` is being fetched. */
  lupaLoading?: boolean;
  /** The episode's saved tasks, for visit-day markers and the LUPA popovers. */
  episodeVisits?: TaskListResponse[];
}

const MISSING_ASSIGNEE_COPY = 'Select an assignee to create these visits.';

const NEW_VISIT_OTHER_TYPE_STYLE = {
  outline: '2px dashed rgb(124 58 237)',
  outlineOffset: '-2px',
  borderRadius: '6px',
};

/**
 * Schedules several visits at once: pick a visit type and click the dates for
 * it, repeat for other types, then create them all with one shared assignee in
 * a single request. Each type's dates are staged live as they are selected;
 * re-selecting a staged type shows its dates highlighted for editing.
 */
export const ScheduleVisitsDialog = ({
  open,
  onOpenChange,
  patientId,
  episodeId,
  episodeStartDate,
  episodeEndDate,
  visitTypeOptions,
  clinicians,
  duplicateClinicianNames,
  onCreated,
  requiresAuthorization = false,
  rogerChartsFrom,
  lupaPeriods,
  lupaLoading = false,
  episodeVisits,
}: ScheduleVisitsDialogProps) => {
  const assigneeFieldId = useId();
  const visitTypeFieldId = useId();
  const canViewSchedulingPointLoads = useCanViewSchedulingPointLoads();
  const { error: showError } = useToast();

  const [assigneeId, setAssigneeId] = useState('');
  // The picker option id: a visit type, or ``alias:<id>`` for an alias.
  const [selectedVisitType, setSelectedVisitType] = useState<string>('');
  const [taskTypeFilter, setTaskTypeFilter] = useState<TaskTypeFilterValue>(
    () => createDefaultTaskTypeFilter()
  );
  const [visitTypeOpen, setVisitTypeOpen] = useState(false);
  const [groups, setGroups] = useState<StagedVisitGroup[]>([]);
  const [isCreating, setIsCreating] = useState(false);
  // The staged visit type whose row is currently hovered; its dates get a light
  // purple background on the calendar so the user can see them at a glance.
  const [hoveredVisitType, setHoveredVisitType] = useState<string | null>(null);

  const resetState = useCallback(() => {
    setAssigneeId('');
    setSelectedVisitType('');
    setTaskTypeFilter(createDefaultTaskTypeFilter());
    setVisitTypeOpen(false);
    setGroups([]);
    setHoveredVisitType(null);
  }, []);

  useEffect(() => {
    if (!open) {
      resetState();
      setIsCreating(false);
    }
  }, [open, resetState]);

  // Show at least two months of dates, and a third when the viewport is wide
  // enough to fit it, so users can pick across months without paging.
  const [monthsToShow, setMonthsToShow] = useState(2);
  useEffect(() => {
    const computeMonths = () => {
      const width = window.innerWidth;
      setMonthsToShow(width >= 1024 ? 3 : width >= 640 ? 2 : 1);
    };
    computeMonths();
    window.addEventListener('resize', computeMonths);
    return () => window.removeEventListener('resize', computeMonths);
  }, []);

  // Only creatable visit types can be scheduled in bulk; orders/communications
  // are single-date tasks created from the Add Task dialog, and legacy visit
  // types would fail the whole batch server-side.
  const visitOnlyOptions = useMemo(
    () =>
      withAliasOptions(visitTypeOptions).filter(
        option => !isOrderType(optionTypeId(option)) && !option.is_legacy
      ),
    [visitTypeOptions]
  );

  const episodeBounds = useMemo(() => {
    const start = episodeStartDate
      ? parseISOToLocalDate(episodeStartDate)
      : null;
    const end = episodeEndDate ? parseISOToLocalDate(episodeEndDate) : null;
    return { start, end };
  }, [episodeStartDate, episodeEndDate]);

  const selectedVisitTypeId = useMemo(() => {
    const option = visitOnlyOptions.find(o => o.id === selectedVisitType);
    return option ? optionTypeId(option) : undefined;
  }, [visitOnlyOptions, selectedVisitType]);

  // A Recert may only be scheduled in days 56-60 of the episode; every day is
  // disabled when the episode ends before day 56.
  const disabledDays = useMemo(() => {
    const matchers: Array<{ before: Date } | { after: Date } | boolean> = [];
    if (isRecertVisitType(selectedVisitTypeId)) {
      const window = recertWindow(episodeStartDate, episodeEndDate);
      const start = window ? parseISOToLocalDate(window.start) : null;
      const end = window ? parseISOToLocalDate(window.end) : null;
      if (!start || !end) return [true];
      return [{ before: start }, { after: end }];
    }
    if (episodeBounds.start) matchers.push({ before: episodeBounds.start });
    if (episodeBounds.end) matchers.push({ after: episodeBounds.end });
    return matchers;
  }, [episodeBounds, episodeStartDate, episodeEndDate, selectedVisitTypeId]);

  const defaultMonth = useMemo(() => {
    const anchor = episodeBounds.start;
    if (!anchor) return undefined;
    return new Date(anchor.getFullYear(), anchor.getMonth(), 1);
  }, [episodeBounds]);

  // Saved visits on the episode, for the same day markers the Tasks calendar draws.
  const visitDays = useMemo(
    () =>
      visitDayKinds(
        episodeBounds.start && episodeBounds.end && episodeVisits
          ? groupVisitsByDate(
              episodeVisits,
              episodeBounds.start,
              episodeBounds.end
            )
          : new Map()
      ),
    [episodeBounds, episodeVisits]
  );
  const markers = useEpisodeCalendarMarkers({
    episodeStartDate,
    episodeEndDate,
    rogerChartsFrom,
    lupaPeriods,
    visitDays,
  });
  const showLupaThresholds = (lupaPeriods?.length ?? 0) > 0 || lupaLoading;

  const clinicianOptions = useMemo(
    () =>
      cliniciansToDropdownOptions(
        clinicians,
        duplicateClinicianNames ?? new Set<string>()
      ),
    [clinicians, duplicateClinicianNames]
  );

  // Staged groups carry the display name the picker showed; fall back to the
  // raw id for a type that is no longer staged.
  const visitTypeLabel = useCallback(
    (visitType: string): string =>
      groups.find(g => g.visitType === visitType)?.displayName ??
      visitOnlyOptions.find(o => o.id === visitType)?.display_name ??
      visitType,
    [groups, visitOnlyOptions]
  );

  const selectedTypeDisplayName = useMemo(
    () =>
      visitOnlyOptions.find(option => option.id === selectedVisitType)
        ?.display_name ?? selectedVisitType,
    [visitOnlyOptions, selectedVisitType]
  );

  // Dates shown/edited on the calendar belong to the currently selected visit
  // type. Picking a type that is already staged surfaces its dates highlighted.
  const selectedDates = useMemo(
    () =>
      groups.find(group => stagedGroupKey(group) === selectedVisitType)
        ?.dates ?? [],
    [groups, selectedVisitType]
  );

  // Dates already staged for the OTHER visit types, so the user can see which
  // days already have a visit (rendered with a dashed purple border) while
  // editing the current type.
  const otherTypeDates = useMemo(
    () =>
      groups
        .filter(group => stagedGroupKey(group) !== selectedVisitType)
        .flatMap(group => group.dates),
    [groups, selectedVisitType]
  );

  // Dates for the hovered staged type, highlighted on the calendar so hovering a
  // row shows its visits at a glance. Empty when nothing is hovered.
  const hoveredDates = useMemo(
    () =>
      groups.find(group => stagedGroupKey(group) === hoveredVisitType)?.dates ??
      [],
    [groups, hoveredVisitType]
  );

  const handleDatesChange = (dates: Date[]) => {
    const option = visitOnlyOptions.find(o => o.id === selectedVisitType);
    if (!option) return;
    setGroups(prev =>
      setDatesForType(
        prev,
        optionTypeId(option) as VisitTypeId,
        selectedTypeDisplayName,
        dates,
        option.alias_id
      )
    );
  };

  const handleRemoveGroup = (key: string) => {
    setGroups(prev => prev.filter(group => stagedGroupKey(group) !== key));
  };

  const totalVisits = countVisits(groups);
  // Create stays disabled without an assignee. Once visits are staged the
  // calendar and visit list push Assignee out of view, so the reason has to
  // live next to Create — a disabled button has no pointer events for a tooltip.
  const missingAssignee = totalVisits > 0 && !assigneeId;

  // Bulk create is one transaction, so a single over-budget date
  // throws the whole batch away. Ask the server which staged visits it would
  // refuse -- it walks the batch the way bulk create does, counting each
  // accepted visit against the authorization it would link to, which a
  // per-date check cannot do. The rule stays in one place; this just renders
  // the answer.
  const [blockedPlannedVisits, setBlockedPlannedVisits] = useState<
    BlockedPlannedVisit[]
  >([]);
  // Only a spent budget can be fixed by staging fewer dates. The other reasons
  // (no authorization covers the date, discipline not on the authorization)
  // block every staged date, so telling the user to remove them sends them
  // deleting rows until the list is empty and Create is still disabled.
  const blockedPlannedVisitsHeading = useMemo(() => {
    const n = blockedPlannedVisits.length;
    const noun = n === 1 ? '1 visit' : `${n} visits`;
    const removable = blockedPlannedVisits.every(
      b => b.reason === 'capacity_exhausted'
    );
    if (removable) {
      return `${noun} ${n === 1 ? 'exceeds' : 'exceed'} the authorization. Remove ${
        n === 1 ? 'it' : 'them'
      } to continue:`;
    }
    return `${noun} cannot be scheduled. Update the authorization to continue:`;
  }, [blockedPlannedVisits]);

  // Staged dates that will count toward each period's LUPA visit count. A
  // new visit's billable status is fixed by its type for this agency.
  const newVisitCounts = useMemo(() => {
    const nonBillableByType = new Map(
      visitTypeOptions.map(option => [option.id, option.non_billable])
    );
    return countNewBillableVisitsByPeriod(
      groups,
      lupaPeriods ?? [],
      visitType => nonBillableByType.get(visitType) === false
    );
  }, [groups, lupaPeriods, visitTypeOptions]);

  const plannedVisits = useMemo(
    () =>
      groups.flatMap(group =>
        group.dates.map(d => ({
          visit_type: group.visitType,
          visit_date: formatDateToISO(d),
        }))
      ),
    [groups]
  );
  const plannedKey = JSON.stringify(plannedVisits);
  useEffect(() => {
    if (!open || !requiresAuthorization || plannedVisits.length === 0) {
      setBlockedPlannedVisits([]);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const res = await checkPlannedVisits({
          path: { episode_id: episodeId },
          body: { visits: plannedVisits },
        });
        if (cancelled) return;
        if (res.error) throw res.error;
        setBlockedPlannedVisits(res.data?.blocked ?? []);
      } catch (error) {
        if (cancelled) return;
        // Fail open: the create path still enforces the block, so a failed
        // check must not disable Create.
        console.error('Failed to check authorization capacity', error);
        setBlockedPlannedVisits([]);
      }
    })();
    return () => {
      cancelled = true;
    };
    // `plannedKey` is the stable identity of `plannedVisits`.
  }, [open, requiresAuthorization, episodeId, plannedKey]);

  const handleCreate = async () => {
    if (totalVisits === 0 || !assigneeId) return;
    if (blockedPlannedVisits.length > 0) return;
    if (countOasisSocVisits(groups) > 1) {
      const message = socConflictCopy('MULTIPLE_SOC_IN_BATCH');
      if (message) showError('Create Failed', message);
      return;
    }
    setIsCreating(true);
    try {
      const visits = buildBulkVisitItems(groups, assigneeId);
      const response =
        await bulkCreatePatientVisitsPatientsPatientIdVisitsBulkPost({
          path: { patient_id: patientId },
          body: {
            episode_id: episodeId,
            visits,
          },
        });
      if (response.error) {
        console.error('Failed to create visits:', response.error);
        const message =
          socConflictCopyFromError(response) ??
          getBusinessErrorMessage(response) ??
          'Failed to create visits.';
        showError('Create Failed', message);
        return;
      }
      await onCreated();
      onOpenChange(false);
    } catch (error) {
      console.error('Error creating visits:', error);
      showError('Create Failed', 'Failed to create visits.');
    } finally {
      setIsCreating(false);
    }
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen && isCreating) return;
    onOpenChange(nextOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className='flex max-h-[calc(100vh-2rem)] w-[calc(100vw-2rem)] flex-col overflow-hidden sm:max-w-3xl lg:max-w-5xl'>
        <DialogHeader>
          <DialogTitle>Schedule Visits</DialogTitle>
          <DialogDescription className='sr-only'>
            Pick a visit type and click its dates, repeat for other types, then
            create every visit at once with one assignee.
          </DialogDescription>
        </DialogHeader>

        <div className='min-h-0 flex-1 space-y-4 overflow-y-auto px-1'>
          <div>
            <DropdownField
              label='Assignee'
              htmlFor={assigneeFieldId}
              required
              variant='formSelect'
              sortOptions
              placeholder='Select assignee'
              options={clinicianOptions}
              value={assigneeId}
              onChange={setAssigneeId}
              disabled={isCreating}
              renderOption={option => {
                const o = option as VirtualizedComboboxOption & {
                  user_email?: string | null;
                  isDup?: boolean;
                };
                return (
                  <>
                    {o.label}
                    {o.isDup && o.user_email && (
                      <span className='ml-1 text-xs text-muted-foreground'>
                        ({o.user_email})
                      </span>
                    )}
                  </>
                );
              }}
            />
            {/* A scheduler who has seen workload beside every other assignee
                picker will read its absence here as a bug or, worse, as "these
                visits are free". Neither is true: one assignee is being given
                dates across several weeks, and a single number cannot answer
                for all of them. Say so rather than leave the gap.

                Gated on the same check as the loads themselves: someone who
                never sees loads anywhere has no absence to explain. */}
            {canViewSchedulingPointLoads && (
              <p className='mt-1.5 text-xs text-muted-foreground'>
                Loads aren&rsquo;t shown here — this schedules more than one
                date.
              </p>
            )}
          </div>

          <div className='rounded-md border border-input p-3'>
            <div>
              <label className='text-sm font-medium' htmlFor={visitTypeFieldId}>
                Visit Type
              </label>
              <div className='mt-1 flex items-center gap-2'>
                <div className='min-w-0 flex-1'>
                  <TaskTypeCombobox
                    id={visitTypeFieldId}
                    options={visitOnlyOptions}
                    value={selectedVisitType}
                    onValueChange={option => setSelectedVisitType(option.id)}
                    filter={taskTypeFilter}
                    onFilterChange={setTaskTypeFilter}
                    tabs={TASK_TYPE_VISIT_FILTER_TABS}
                    modal
                    disabled={isCreating}
                    placeholder='Select visit type'
                    open={visitTypeOpen}
                    onOpenChange={setVisitTypeOpen}
                  />
                </div>
                {groups.length > 0 && (
                  <Button
                    type='button'
                    variant='outline'
                    className='shrink-0'
                    onClick={() => {
                      setSelectedVisitType('');
                      setTaskTypeFilter(createDefaultTaskTypeFilter());
                      setVisitTypeOpen(true);
                    }}
                    disabled={isCreating}
                  >
                    <Plus className='mr-1 h-4 w-4' />
                    Add another
                  </Button>
                )}
              </div>
            </div>

            <div className='mt-3'>
              <div className='flex items-center justify-between'>
                <label className='text-sm font-medium'>Dates</label>
                <span className='text-xs text-muted-foreground'>
                  {selectedVisitType
                    ? `${selectedDates.length} selected`
                    : 'Select a visit type first'}
                </span>
              </div>
              {/* Above the calendar so the new-visit counts stay in view
                  while dates are clicked. */}
              {showLupaThresholds && (
                <div className='mt-2' data-testid='schedule-visits-lupa'>
                  <div className='mb-2 select-none text-xs font-semibold uppercase tracking-wider text-foreground'>
                    LUPA thresholds
                  </div>
                  {lupaLoading ? (
                    <Skeleton className='h-10 w-full' />
                  ) : (
                    <LupaThresholdGauge
                      periods={lupaPeriods ?? []}
                      visits={episodeVisits}
                      newVisitCounts={newVisitCounts}
                      className='grid gap-3 md:grid-cols-2 md:gap-6'
                    />
                  )}
                </div>
              )}
              <div className='mt-2 flex justify-center overflow-x-auto rounded-md border border-input'>
                <Calendar
                  mode='multiple'
                  numberOfMonths={monthsToShow}
                  pagedNavigation
                  selected={selectedDates}
                  onSelect={dates => handleDatesChange(dates ?? [])}
                  defaultMonth={defaultMonth}
                  disabled={disabledDays}
                  showOutsideDays={false}
                  classNames={markers.classNames}
                  modifiers={{
                    ...markers.modifiers,
                    scheduledOtherType: otherTypeDates,
                    hoveredType: hoveredDates,
                  }}
                  modifiersClassNames={markers.modifiersClassNames}
                  modifiersStyles={{
                    ...markers.modifiersStyles,
                    scheduledOtherType: NEW_VISIT_OTHER_TYPE_STYLE,
                    hoveredType: {
                      backgroundColor: 'rgb(221 214 254)',
                      borderRadius: '6px',
                    },
                  }}
                />
              </div>
              <EpisodeCalendarLegend
                hasTwoPdgmPeriods={markers.hasTwoPdgmPeriods}
                showChartsFromCutoff={markers.showChartsFromCutoff}
              >
                <EpisodeCalendarLegendItem
                  swatch={<div className='h-3 w-3 rounded-sm bg-primary' />}
                >
                  New visit
                </EpisodeCalendarLegendItem>
                <EpisodeCalendarLegendItem
                  swatch={
                    <div
                      className='h-3 w-3 rounded-sm'
                      style={{
                        outline: NEW_VISIT_OTHER_TYPE_STYLE.outline,
                        outlineOffset: NEW_VISIT_OTHER_TYPE_STYLE.outlineOffset,
                      }}
                    />
                  }
                >
                  New visit, other type
                </EpisodeCalendarLegendItem>
              </EpisodeCalendarLegend>
            </div>
          </div>

          {groups.length > 0 && (
            <div className='space-y-2'>
              {groups.map(group => {
                const groupKey = stagedGroupKey(group);
                const isActive = groupKey === selectedVisitType;
                return (
                  <div
                    key={groupKey}
                    role='button'
                    tabIndex={0}
                    onClick={() => setSelectedVisitType(groupKey)}
                    onMouseEnter={() => setHoveredVisitType(groupKey)}
                    onMouseLeave={() => setHoveredVisitType(null)}
                    onFocus={() => setHoveredVisitType(groupKey)}
                    onBlur={() => setHoveredVisitType(null)}
                    onKeyDown={event => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        setSelectedVisitType(groupKey);
                      }
                    }}
                    className={`flex cursor-pointer items-start justify-between gap-2 rounded-md px-3 py-2 ${
                      isActive
                        ? 'bg-primary/10 ring-1 ring-primary/40'
                        : 'bg-muted/40 hover:bg-muted/60'
                    }`}
                  >
                    <div className='min-w-0'>
                      <div className='flex items-center gap-2'>
                        <span className='text-sm font-medium'>
                          {group.displayName}
                        </span>
                        <Badge variant='secondary' className='text-xs'>
                          {group.dates.length}
                        </Badge>
                      </div>
                      <div className='mt-0.5 text-xs text-muted-foreground'>
                        {group.dates
                          .map(date => formatDateToMMDDYYYY(date))
                          .join(', ')}
                      </div>
                    </div>
                    <Button
                      type='button'
                      variant='ghost'
                      size='sm'
                      className='h-6 w-6 shrink-0 p-0'
                      onClick={event => {
                        event.stopPropagation();
                        handleRemoveGroup(groupKey);
                      }}
                      disabled={isCreating}
                      aria-label={`Remove ${group.displayName}`}
                    >
                      <X className='h-4 w-4' />
                    </Button>
                  </div>
                );
              })}
            </div>
          )}

          {blockedPlannedVisits.length > 0 && (
            <div
              role='alert'
              className='rounded border border-destructive/20 bg-destructive/10 px-3 py-2 text-sm text-destructive'
            >
              <p className='font-medium'>{blockedPlannedVisitsHeading}</p>
              <ul className='mt-1 list-disc space-y-0.5 pl-5'>
                {blockedPlannedVisits.map(item => (
                  <li key={`${item.visit_type}-${item.visit_date}`}>
                    {visitTypeLabel(item.visit_type)} on{' '}
                    {formatDateToMMDDYYYY(item.visit_date)} — {item.message}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <DialogFooter
          className={
            missingAssignee
              ? 'flex-col sm:items-center sm:justify-between'
              : undefined
          }
        >
          {missingAssignee && (
            <p
              id='schedule-visits-missing-assignee'
              role='status'
              className='flex items-start gap-2 text-sm text-destructive sm:max-w-xs'
            >
              <AlertCircle className='mt-0.5 h-4 w-4 shrink-0' aria-hidden />
              <span>{MISSING_ASSIGNEE_COPY}</span>
            </p>
          )}
          <div className='flex flex-col-reverse gap-2 sm:flex-row'>
            <Button
              variant='outline'
              onClick={() => handleOpenChange(false)}
              disabled={isCreating}
            >
              Cancel
            </Button>
            <Button
              onClick={() => void handleCreate()}
              aria-describedby={
                missingAssignee ? 'schedule-visits-missing-assignee' : undefined
              }
              disabled={
                isCreating ||
                totalVisits === 0 ||
                !assigneeId ||
                blockedPlannedVisits.length > 0
              }
            >
              {isCreating
                ? 'Creating...'
                : totalVisits > 0
                  ? `Create ${totalVisits} ${totalVisits === 1 ? 'visit' : 'visits'}`
                  : 'Create visits'}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ScheduleVisitsDialog;
